import type { HttpErrorType } from '@/core/api/http-errors';
import { CLIENTS_ERROR_MAP } from '@/features/clients/public';
import {
  INVITATIONS_ERROR_MAP,
  MEMBERS_ERROR_MAP,
  MEMBERS_HIRE_DATE_ERROR_MAP,
} from '@/features/members/public';
import { BOARD_GROUPS_ERROR_MAP, BOARD_VIEWS_ERROR_MAP } from '@/features/boards/public';
import { WHITEBOARDS_ERROR_MAP } from '@/features/whiteboards/public';
import {
  ORGANIZATIONS_ERROR_MAP,
  ONBOARDING_ERROR_MAP,
  ORG_AVATAR_ERROR_MAP,
} from '@/features/organizations/public';
import { ORGANIZATION_UNITS_ERROR_MAP } from '@/features/organization-units/public';
import {
  PROJECT_MEMBERS_ERROR_MAP,
  PROJECTS_ERROR_MAP,
  PROJECT_ICON_ERROR_MAP,
} from '@/features/projects/public';
import { TAGS_ERROR_MAP } from '@/features/tags/public';
import {
  FORMS_CREATE_ERROR_MAP,
  FORMS_UPDATE_ERROR_MAP,
  FORMS_DELETE_ERROR_MAP,
  FORMS_DUPLICATE_ERROR_MAP,
  FORMS_LIFECYCLE_ERROR_MAP,
  FORMS_SHARING_ERROR_MAP,
  FORMS_SUBMIT_ERROR_MAP,
  FORMS_PUBLIC_READ_ERROR_MAP,
  FORMS_CREATE_TASK_ERROR_MAP,
  FORMS_DELETE_SUBMISSION_ERROR_MAP,
  FORMS_SAVE_AS_TEMPLATE_ERROR_MAP,
  TEMPLATES_CREATE_ERROR_MAP,
  TEMPLATES_UPDATE_ERROR_MAP,
  TEMPLATES_DELETE_ERROR_MAP,
  TEMPLATES_DUPLICATE_ERROR_MAP,
  TEMPLATES_INSTANTIATE_ERROR_MAP,
} from '@/features/forms/public';
import { CUSTOM_FIELDS_ERROR_MAP } from '@/features/custom-fields/public';
import {
  PROJECT_TEMPLATES_CREATE_ERROR_MAP,
  PROJECT_TEMPLATES_UPDATE_ERROR_MAP,
  PROJECT_TEMPLATES_DELETE_ERROR_MAP,
  PROJECT_TEMPLATES_INSTANTIATE_ERROR_MAP,
  PROJECT_TEMPLATES_SAVE_ERROR_MAP,
} from '@/features/project-templates/public';
import { CHECKLISTS_ERROR_MAP, CHECKLIST_ITEMS_ERROR_MAP } from '@/features/checklists/public';
import {
  TIME_TRACKING_START_ERROR_MAP,
  TIME_TRACKING_STOP_ERROR_MAP,
  MANUAL_ENTRY_ERROR_MAP,
  TIME_ENTRY_ERROR_MAP,
} from '@/features/time-tracking/public';
import {
  TASK_UPDATE_ERROR_MAP,
  TASK_CREATE_ERROR_MAP,
  TASK_DELETE_ERROR_MAP,
  TASK_MOVE_ERROR_MAP,
  TASK_ASSIGN_ERROR_MAP,
  TASK_PRIORITY_ERROR_MAP,
  TASK_TOGGLE_ERROR_MAP,
  TASK_REORDER_ERROR_MAP,
  TASK_SUBTASK_CREATE_ERROR_MAP,
  TASK_ATTACHMENTS_ERROR_MAP,
  TASK_TAGS_ERROR_MAP,
  TASK_QUERY_ERROR_MAP,
  TASK_COMMENT_ERROR_MAP,
  TASK_ARCHIVE_ERROR_MAP,
  TASK_COPY_ERROR_MAP,
  TASK_BULK_EDIT_ERROR_MAP,
  TASK_BULK_DELETE_ERROR_MAP,
  TASK_FIELD_VALUE_ERROR_MAP,
  TASK_IMPORT_ERROR_MAP,
  TASK_DEPENDENCY_CREATE_ERROR_MAP,
  TASK_DEPENDENCY_DELETE_ERROR_MAP,
} from '@/features/tasks/public';
import { PROFILE_ERROR_MAP } from '@/features/settings/public';
import {
  ACCEPT_INVITATION_ERROR_MAP,
  REJECT_INVITATION_ERROR_MAP,
} from '@/features/invitations/public';
import { MARK_READ_ERROR_MAP, MARK_ALL_READ_ERROR_MAP } from '@/features/notifications/public';
import {
  DASHBOARD_WIDGET_CREATE_ERROR_MAP,
  DASHBOARD_WIDGET_UPDATE_ERROR_MAP,
  DASHBOARD_WIDGET_DELETE_ERROR_MAP,
  DASHBOARD_WIDGET_REORDER_ERROR_MAP,
} from '@/features/dashboard/errors';
import {
  PROJECT_FILE_UPLOAD_ERROR_MAP,
  PROJECT_FILE_DELETE_ERROR_MAP,
  PROJECT_FILE_MOVE_ERROR_MAP,
  PROJECT_FOLDER_CREATE_ERROR_MAP,
  PROJECT_FOLDER_DELETE_ERROR_MAP,
  PROJECT_FOLDER_RENAME_ERROR_MAP,
  PROJECT_FOLDER_MOVE_ERROR_MAP,
  PROJECT_FILE_FAVORITE_ERROR_MAP,
  PROJECT_FILE_RENAME_ERROR_MAP,
  PROJECT_FILE_VISIBILITY_ERROR_MAP,
  PROJECT_FILE_BULK_DELETE_ERROR_MAP,
} from '@/features/project-files/errors';
import {
  AI_PROPOSE_ERROR_MAP,
  AI_APPLY_ERROR_MAP,
  AI_TASK_PLAN_PROPOSE_ERROR_MAP,
  AI_TASK_PLAN_APPLY_ERROR_MAP,
} from '@/features/ai/errors';
import {
  WORK_SCHEDULE_TENANT_ERROR_MAP,
  MEMBER_WORK_SCHEDULE_ERROR_MAP,
} from '@/features/work-schedules/public';
import {
  NOTES_CREATE_ERROR_MAP,
  NOTES_UPDATE_ERROR_MAP,
  NOTES_DELETE_ERROR_MAP,
  NOTES_HISTORY_ERROR_MAP,
  NOTES_VERSION_ERROR_MAP,
  NOTES_RESTORE_ERROR_MAP,
  NOTES_EXPORT_ERROR_MAP,
  NOTES_CONVERT_TO_TASK_ERROR_MAP,
} from '@/features/notes/public';
import {
  CALENDAR_DELETE_CONNECTION_ERROR_MAP,
  CALENDAR_UPDATE_LINKED_CALENDAR_ERROR_MAP,
  CALENDAR_AUTHORIZE_ERROR_MAP,
  CALENDAR_SYNC_ERROR_MAP,
} from '@/features/calendar/public';
import {
  TIME_OFF_POLICY_ERROR_MAP,
  TIME_OFF_TYPES_ERROR_MAP,
  TIME_OFF_MATRIX_ERROR_MAP,
  TIME_OFF_HOLIDAYS_ERROR_MAP,
  TIME_OFF_CYCLES_ERROR_MAP,
  TIME_OFF_ADJUSTMENTS_ERROR_MAP,
  TIME_OFF_REQUESTS_CREATE_ERROR_MAP,
  TIME_OFF_APPROVALS_ERROR_MAP,
  TIME_OFF_OVERRIDE_ERROR_MAP,
  TIME_OFF_CANCEL_ERROR_MAP,
  TIME_OFF_ATTACHMENTS_ERROR_MAP,
  TIME_OFF_TEAM_ERROR_MAP,
  TIME_OFF_ADMIN_SUMMARY_ERROR_MAP,
  TIME_OFF_ADMIN_UPCOMING_ERROR_MAP,
  TIME_OFF_HISTORICAL_REQUESTS_ERROR_MAP,
} from '@/features/time-off/public';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Maps error keys to i18n title keys. Keys can be:
 * - A backend error code (e.g. "Area.NotFound", "Member.AlreadyExists")
 * - An HttpErrorType (e.g. "conflict", "validation")
 * - "default" — required catch-all
 *
 * Lookup order: backend error code → HttpErrorType → default
 */
export type ErrorTypeMap = Record<string, string> & { default: string };

/** Maps each HTTP method to its ErrorTypeMap. */
export type ErrorMap = Partial<Record<HttpMethod, ErrorTypeMap>>;

export interface ErrorRegistryEntry {
  /** Regex matched against the request URL to identify the feature. */
  pattern: RegExp;
  errors: ErrorMap;
}

/**
 * Central registry of feature error maps, ordered from most specific to least specific.
 * The last entry acts as the global fallback (catch-all pattern).
 *
 * @remarks
 * Feature entries are registered here as each feature implements its errors.ts.
 * The global fallback uses core i18n keys (core:errors.*).
 */
export const ERROR_REGISTRY: ErrorRegistryEntry[] = [
  { pattern: /\/api\/identity\/me/, errors: PROFILE_ERROR_MAP },
  { pattern: /\/api\/notifications\/[^/]+\/read$/, errors: MARK_READ_ERROR_MAP },
  { pattern: /\/api\/notifications\/read-all$/, errors: MARK_ALL_READ_ERROR_MAP },
  { pattern: /\/api\/invitations\/accept/, errors: ACCEPT_INVITATION_ERROR_MAP },
  { pattern: /\/api\/invitations\/[^/]+\/reject/, errors: REJECT_INVITATION_ERROR_MAP },
  { pattern: /\/api\/me\/invitations\/[^/]+\/accept/, errors: ACCEPT_INVITATION_ERROR_MAP },
  { pattern: /\/api\/me\/invitations\/[^/]+\/reject/, errors: REJECT_INVITATION_ERROR_MAP },
  { pattern: /\/api\/me\/invitations/, errors: REJECT_INVITATION_ERROR_MAP },
  { pattern: /\/api\/onboarding/, errors: ONBOARDING_ERROR_MAP },
  { pattern: /^\/api\/tenants$/, errors: ORGANIZATIONS_ERROR_MAP },
  { pattern: /\/api\/tenants\/areas/, errors: ORGANIZATION_UNITS_ERROR_MAP },
  { pattern: /\/api\/projects\/[^/]+\/members/, errors: PROJECT_MEMBERS_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/time\/start/, errors: TIME_TRACKING_START_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/time\/stop/, errors: TIME_TRACKING_STOP_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/time$/, errors: MANUAL_ENTRY_ERROR_MAP },
  { pattern: /\/api\/time\/[^/]+$/, errors: TIME_ENTRY_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/archive/, errors: TASK_ARCHIVE_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/unarchive/, errors: TASK_ARCHIVE_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/copy/, errors: TASK_COPY_ERROR_MAP },
  {
    pattern: /\/api\/tasks\/bulk/,
    errors: { ...TASK_BULK_EDIT_ERROR_MAP, ...TASK_BULK_DELETE_ERROR_MAP },
  },
  { pattern: /\/api\/tasks\/[^/]+\/toggle/, errors: TASK_TOGGLE_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/move/, errors: TASK_MOVE_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/assign/, errors: TASK_ASSIGN_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/priority/, errors: TASK_PRIORITY_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/subtasks/, errors: TASK_SUBTASK_CREATE_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/attachments/, errors: TASK_ATTACHMENTS_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/tags/, errors: TASK_TAGS_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/comments/, errors: TASK_COMMENT_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/checklists/, errors: CHECKLISTS_ERROR_MAP },
  { pattern: /\/api\/tasks\/[^/]+\/field-values/, errors: TASK_FIELD_VALUE_ERROR_MAP },
  {
    pattern: /\/api\/checklists\/items\/[^/]+\/(convert-to-task|convert-to-subtask|move)/,
    errors: CHECKLIST_ITEMS_ERROR_MAP,
  },
  { pattern: /\/api\/checklists\/items\/[^/]+$/, errors: CHECKLIST_ITEMS_ERROR_MAP },
  { pattern: /\/api\/checklists\/[^/]+\/items/, errors: CHECKLIST_ITEMS_ERROR_MAP },
  { pattern: /\/api\/checklists\/[^/]+$/, errors: CHECKLISTS_ERROR_MAP },
  { pattern: /\/api\/tasks\/reorder/, errors: TASK_REORDER_ERROR_MAP },
  {
    pattern: /\/api\/tasks\/[^/]+$/,
    errors: {
      ...TASK_UPDATE_ERROR_MAP,
      ...TASK_DELETE_ERROR_MAP,
    },
  },
  { pattern: /\/api\/tasks$/, errors: TASK_CREATE_ERROR_MAP },
  { pattern: /\/api\/projects\/[^/]+\/tasks\/import/, errors: TASK_IMPORT_ERROR_MAP },
  {
    pattern: /\/api\/projects\/[^/]+\/tasks\/[^/]+\/dependencies/,
    errors: { ...TASK_DEPENDENCY_CREATE_ERROR_MAP, ...TASK_DEPENDENCY_DELETE_ERROR_MAP },
  },
  { pattern: /\/api\/projects\/[^/]+\/tasks/, errors: TASK_QUERY_ERROR_MAP },
  { pattern: /\/api\/projects\/[^/]+\/fields/, errors: CUSTOM_FIELDS_ERROR_MAP },
  { pattern: /\/api\/projects\/[^/]+\/tags/, errors: TAGS_ERROR_MAP },
  { pattern: /\/api\/projects\/[^/]+\/icon/, errors: PROJECT_ICON_ERROR_MAP },
  { pattern: /\/api\/projects\/[^/]+\/files\/[^/]+\/move/, errors: PROJECT_FILE_MOVE_ERROR_MAP },
  {
    pattern: /\/api\/projects\/[^/]+\/files\/[^/]+\/favorite/,
    errors: PROJECT_FILE_FAVORITE_ERROR_MAP,
  },
  {
    pattern: /\/api\/projects\/[^/]+\/files\/[^/]+\/rename/,
    errors: PROJECT_FILE_RENAME_ERROR_MAP,
  },
  {
    pattern: /\/api\/projects\/[^/]+\/files\/[^/]+\/visibility/,
    errors: PROJECT_FILE_VISIBILITY_ERROR_MAP,
  },
  {
    pattern: /\/api\/projects\/[^/]+\/files\/[^/]+$/,
    errors: { ...PROJECT_FILE_DELETE_ERROR_MAP },
  },
  {
    pattern: /\/api\/projects\/[^/]+\/files$/,
    errors: { ...PROJECT_FILE_UPLOAD_ERROR_MAP, ...PROJECT_FILE_BULK_DELETE_ERROR_MAP },
  },
  {
    pattern: /\/api\/projects\/[^/]+\/folders\/[^/]+\/move/,
    errors: PROJECT_FOLDER_MOVE_ERROR_MAP,
  },
  {
    pattern: /\/api\/projects\/[^/]+\/folders\/[^/]+\/rename/,
    errors: PROJECT_FOLDER_RENAME_ERROR_MAP,
  },
  {
    pattern: /\/api\/projects\/[^/]+\/folders\/[^/]+$/,
    errors: { ...PROJECT_FOLDER_DELETE_ERROR_MAP },
  },
  { pattern: /\/api\/projects\/[^/]+\/folders/, errors: PROJECT_FOLDER_CREATE_ERROR_MAP },
  // Dashboards come in three URL shapes — the tenant one has no id segment
  // (`/tenants/dashboard`), project and board carry theirs. They must stay
  // above the broad `/api/projects` entry, which would otherwise swallow them.
  {
    pattern: /\/api\/(tenants|(projects|boards)\/[^/]+)\/dashboard\/widgets\/reorder$/,
    errors: DASHBOARD_WIDGET_REORDER_ERROR_MAP,
  },
  {
    pattern: /\/api\/(tenants|(projects|boards)\/[^/]+)\/dashboard\/widgets\/[^/]+$/,
    errors: { ...DASHBOARD_WIDGET_UPDATE_ERROR_MAP, ...DASHBOARD_WIDGET_DELETE_ERROR_MAP },
  },
  {
    pattern: /\/api\/(tenants|(projects|boards)\/[^/]+)\/dashboard\/widgets$/,
    errors: DASHBOARD_WIDGET_CREATE_ERROR_MAP,
  },
  {
    pattern: /\/api\/projects\/[^/]+\/boards\/[^/]+\/ai\/task-plan\/proposal/,
    errors: AI_TASK_PLAN_PROPOSE_ERROR_MAP,
  },
  {
    pattern: /\/api\/projects\/[^/]+\/boards\/[^/]+\/ai\/task-plan/,
    errors: AI_TASK_PLAN_APPLY_ERROR_MAP,
  },
  {
    pattern: /\/api\/projects\/[^/]+\/boards\/[^/]+\/ai\/tasks\/proposal/,
    errors: AI_PROPOSE_ERROR_MAP,
  },
  {
    pattern: /\/api\/projects\/[^/]+\/boards\/[^/]+\/ai\/tasks/,
    errors: AI_APPLY_ERROR_MAP,
  },
  { pattern: /\/api\/projects/, errors: PROJECTS_ERROR_MAP },
  { pattern: /\/api\/tenants\/[^/]+\/avatar/, errors: ORG_AVATAR_ERROR_MAP },
  { pattern: /\/api\/tenants\/invitations/, errors: INVITATIONS_ERROR_MAP },
  { pattern: /\/api\/tenants\/members\/[^/]+\/hire-date/, errors: MEMBERS_HIRE_DATE_ERROR_MAP },
  { pattern: /\/api\/tenants\/members/, errors: MEMBERS_ERROR_MAP },
  { pattern: /\/api\/tenants\/clients/, errors: CLIENTS_ERROR_MAP },
  // Both shapes: `/boards/{id}/whiteboards` (list, create) and `/whiteboards/{id}` (the rest)
  { pattern: /\/api\/(boards\/[^/]+\/)?whiteboards/, errors: WHITEBOARDS_ERROR_MAP },
  { pattern: /\/api\/boards\/[^/]+\/views/, errors: BOARD_VIEWS_ERROR_MAP },
  { pattern: /\/api\/boards\/[^/]+\/groups/, errors: BOARD_GROUPS_ERROR_MAP },
  { pattern: /\/api\/work-schedules\/tenant/, errors: WORK_SCHEDULE_TENANT_ERROR_MAP },
  { pattern: /\/api\/work-schedules\/members/, errors: MEMBER_WORK_SCHEDULE_ERROR_MAP },
  {
    pattern: /\/api\/forms\/[^/]+\/submissions\/[^/]+\/create-task/,
    errors: FORMS_CREATE_TASK_ERROR_MAP,
  },
  {
    pattern: /\/api\/forms\/[^/]+\/submissions\/[^/]+$/,
    errors: FORMS_DELETE_SUBMISSION_ERROR_MAP,
  },
  { pattern: /\/api\/forms\/[^/]+\/submit/, errors: FORMS_SUBMIT_ERROR_MAP },
  { pattern: /\/api\/forms\/[^/]+\/public$/, errors: FORMS_PUBLIC_READ_ERROR_MAP },
  { pattern: /\/api\/forms\/access\/[^/]+$/, errors: FORMS_PUBLIC_READ_ERROR_MAP },
  {
    pattern: /\/api\/forms\/[^/]+\/(sharing|share)/,
    errors: FORMS_SHARING_ERROR_MAP,
  },
  {
    pattern: /\/api\/forms\/[^/]+\/(close|archive)/,
    errors: FORMS_LIFECYCLE_ERROR_MAP,
  },
  { pattern: /\/api\/forms\/[^/]+\/duplicate/, errors: FORMS_DUPLICATE_ERROR_MAP },
  { pattern: /\/api\/forms\/[^/]+\/save-as-template/, errors: FORMS_SAVE_AS_TEMPLATE_ERROR_MAP },
  {
    pattern: /\/api\/forms\/[^/]+$/,
    errors: { ...FORMS_UPDATE_ERROR_MAP, ...FORMS_DELETE_ERROR_MAP },
  },
  { pattern: /\/api\/forms$/, errors: FORMS_CREATE_ERROR_MAP },
  { pattern: /\/api\/templates\/[^/]+\/duplicate/, errors: TEMPLATES_DUPLICATE_ERROR_MAP },
  { pattern: /\/api\/templates\/[^/]+\/instantiate/, errors: TEMPLATES_INSTANTIATE_ERROR_MAP },
  {
    pattern: /\/api\/templates\/[^/]+$/,
    errors: { ...TEMPLATES_UPDATE_ERROR_MAP, ...TEMPLATES_DELETE_ERROR_MAP },
  },
  { pattern: /\/api\/templates$/, errors: TEMPLATES_CREATE_ERROR_MAP },
  { pattern: /\/api\/projects\/[^/]+\/save-as-template/, errors: PROJECT_TEMPLATES_SAVE_ERROR_MAP },
  {
    pattern: /\/api\/project-templates\/[^/]+\/instantiate/,
    errors: PROJECT_TEMPLATES_INSTANTIATE_ERROR_MAP,
  },
  {
    pattern: /\/api\/project-templates\/[^/]+\/structure/,
    errors: PROJECT_TEMPLATES_UPDATE_ERROR_MAP,
  },
  {
    pattern: /\/api\/project-templates\/[^/]+$/,
    errors: { ...PROJECT_TEMPLATES_UPDATE_ERROR_MAP, ...PROJECT_TEMPLATES_DELETE_ERROR_MAP },
  },
  { pattern: /\/api\/project-templates$/, errors: PROJECT_TEMPLATES_CREATE_ERROR_MAP },
  {
    pattern: /\/api\/notes\/[^/]+\/history\/[^/]+\/restore/,
    errors: NOTES_RESTORE_ERROR_MAP,
  },
  { pattern: /\/api\/notes\/[^/]+\/history\/[^/]+$/, errors: NOTES_VERSION_ERROR_MAP },
  { pattern: /\/api\/notes\/[^/]+\/history/, errors: NOTES_HISTORY_ERROR_MAP },
  { pattern: /\/api\/notes\/[^/]+\/export/, errors: NOTES_EXPORT_ERROR_MAP },
  {
    pattern: /\/api\/notes\/[^/]+\/convert-to-task/,
    errors: NOTES_CONVERT_TO_TASK_ERROR_MAP,
  },
  {
    pattern: /\/api\/notes\/[^/]+$/,
    errors: { ...NOTES_UPDATE_ERROR_MAP, ...NOTES_DELETE_ERROR_MAP },
  },
  { pattern: /\/api\/notes$/, errors: NOTES_CREATE_ERROR_MAP },
  {
    pattern: /\/api\/calendar\/connections\/[^/]+\/sync/,
    errors: CALENDAR_SYNC_ERROR_MAP,
  },
  {
    pattern: /\/api\/calendar\/connections\/[^/]+\/calendars\/[^/]+/,
    errors: CALENDAR_UPDATE_LINKED_CALENDAR_ERROR_MAP,
  },
  {
    pattern: /\/api\/calendar\/connections\/[^/]+\/authorize/,
    errors: CALENDAR_AUTHORIZE_ERROR_MAP,
  },
  {
    pattern: /\/api\/calendar\/connections\/[^/]+$/,
    errors: CALENDAR_DELETE_CONNECTION_ERROR_MAP,
  },
  { pattern: /\/api\/time-off\/policy/, errors: TIME_OFF_POLICY_ERROR_MAP },
  { pattern: /\/api\/time-off\/types/, errors: TIME_OFF_TYPES_ERROR_MAP },
  { pattern: /\/api\/time-off\/seniority-matrix/, errors: TIME_OFF_MATRIX_ERROR_MAP },
  { pattern: /\/api\/time-off\/holidays/, errors: TIME_OFF_HOLIDAYS_ERROR_MAP },
  {
    pattern: /\/api\/time-off\/members\/[^/]+\/cycles/,
    errors: TIME_OFF_CYCLES_ERROR_MAP,
  },
  {
    pattern: /\/api\/time-off\/members\/[^/]+\/adjustments/,
    errors: TIME_OFF_ADJUSTMENTS_ERROR_MAP,
  },
  { pattern: /\/api\/time-off\/requests$/, errors: TIME_OFF_REQUESTS_CREATE_ERROR_MAP },
  {
    pattern: /\/api\/time-off\/requests\/[^/]+\/(approve|reject)/,
    errors: TIME_OFF_APPROVALS_ERROR_MAP,
  },
  {
    pattern: /\/api\/time-off\/requests\/[^/]+\/override/,
    errors: TIME_OFF_OVERRIDE_ERROR_MAP,
  },
  {
    pattern: /\/api\/time-off\/requests\/[^/]+\/attachments/,
    errors: TIME_OFF_ATTACHMENTS_ERROR_MAP,
  },
  {
    pattern: /\/api\/time-off\/requests\/[^/]+(\/days\/[^/]+)?$/,
    errors: TIME_OFF_CANCEL_ERROR_MAP,
  },
  { pattern: /\/api\/time-off\/team/, errors: TIME_OFF_TEAM_ERROR_MAP },
  { pattern: /\/api\/time-off\/admin\/summary/, errors: TIME_OFF_ADMIN_SUMMARY_ERROR_MAP },
  { pattern: /\/api\/time-off\/admin\/upcoming/, errors: TIME_OFF_ADMIN_UPCOMING_ERROR_MAP },
  {
    pattern: /\/api\/time-off\/admin\/historical-requests/,
    errors: TIME_OFF_HISTORICAL_REQUESTS_ERROR_MAP,
  },

  // Global fallback — must remain last
  {
    pattern: /.*/,
    errors: {
      POST: { default: 'core:errors.server.title' },
      PUT: { default: 'core:errors.server.title' },
      PATCH: { default: 'core:errors.server.title' },
      DELETE: { default: 'core:errors.server.title' },
    },
  },
];

/**
 * Looks up the i18n key for a given URL, HTTP method, error type, and optional backend error code.
 *
 * Lookup order:
 * 1. Backend error code (e.g. "Area.NotFound") — most specific
 * 2. HttpErrorType (e.g. "conflict") — type-level fallback
 * 3. "default" entry in the method map — feature-level fallback
 * 4. core i18n key — global fallback
 */
export function lookupRegistry(
  url: string,
  method: string,
  errorType: HttpErrorType,
  errorCode: string | null = null,
): { titleKey: string; descriptionKey: string } {
  const normalizedMethod = method.toUpperCase() as HttpMethod;

  // Network and timeout are always global — no feature context adds value
  if (errorType === 'network' || errorType === 'timeout') {
    return {
      titleKey: `core:errors.${errorType}.title`,
      descriptionKey: `core:errors.${errorType}.description`,
    };
  }

  const entry = ERROR_REGISTRY.find(({ pattern }) => pattern.test(url));
  const methodMap = entry?.errors[normalizedMethod];
  const titleKey =
    (errorCode ? methodMap?.[errorCode] : undefined) ??
    methodMap?.[errorType] ??
    methodMap?.default ??
    `core:errors.${errorType}.title`;

  return {
    titleKey,
    descriptionKey: `core:errors.${errorType}.description`,
  };
}
